import { useEffect, useId, useState, type InputHTMLAttributes } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  scorePassword,
  STRENGTH_LABELS,
  STRENGTH_COLORS,
  type PasswordScore,
} from "@/lib/passwordStrength";

interface PasswordFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: string;
  error?: string;
  hint?: string;
  requiredMark?: boolean;
  /** Show the live strength meter. Off for login/confirm fields. */
  showMeter?: boolean;
  /** Extra context (email, name, subdomain) so zxcvbn penalises derived passwords. */
  userInputs?: string[];
  /** Reports the estimated strength as the user types, so the parent can gate submit. */
  onScoreChange?: (score: PasswordScore) => void;
}

export function PasswordField({
  label,
  error,
  hint,
  requiredMark,
  showMeter = false,
  userInputs = [],
  onScoreChange,
  className,
  id,
  value,
  ...props
}: PasswordFieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [visible, setVisible] = useState(false);
  const [strength, setStrength] = useState<{
    score: PasswordScore;
    suggestion: string | null;
    warning: string | null;
  } | null>(null);

  const password = typeof value === "string" ? value : "";

  // Debounced strength scoring — zxcvbn is loaded lazily on first keystroke.
  useEffect(() => {
    if (!showMeter) return;
    if (!password) {
      setStrength(null);
      onScoreChange?.(0);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      void scorePassword(password, userInputs).then((res) => {
        if (!alive) return;
        setStrength(res);
        onScoreChange?.(res.score);
      });
    }, 150);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // onScoreChange/userInputs are intentionally excluded — parents pass fresh
    // closures each render and we only care about the password value changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, showMeter]);

  const describedBy = error
    ? `${inputId}-error`
    : hint
      ? `${inputId}-hint`
      : undefined;

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-slate-700">
          {label}
          {requiredMark && <span className="text-rose-500"> *</span>}
        </label>
      )}
      <div className="relative">
        <input
          id={inputId}
          type={visible ? "text" : "password"}
          value={value}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            "block w-full rounded-lg border bg-white px-3 py-2 pr-10 text-sm text-slate-900 placeholder:text-slate-400",
            "focus:border-brand-500 focus:ring-2 focus:ring-brand-100",
            error ? "border-rose-400" : "border-slate-300",
            className,
          )}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          tabIndex={-1}
          aria-label={visible ? "Sembunyikan kata laluan" : "Tunjuk kata laluan"}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600"
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>

      {showMeter && strength && (
        <div className="space-y-1">
          <div className="flex gap-1" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className={cn(
                  "h-1 flex-1 rounded-full",
                  i <= strength.score ? STRENGTH_COLORS[strength.score] : "bg-slate-200",
                )}
              />
            ))}
          </div>
          <p className="text-xs text-slate-500">
            Kekuatan:{" "}
            <span className="font-medium text-slate-700">
              {STRENGTH_LABELS[strength.score]}
            </span>
            {strength.warning ? ` — ${strength.warning}` : ""}
          </p>
          {strength.suggestion && strength.score < 3 && (
            <p className="text-xs text-slate-400">{strength.suggestion}</p>
          )}
        </div>
      )}

      {error ? (
        <p id={`${inputId}-error`} className="text-xs font-medium text-rose-600">
          {error}
        </p>
      ) : hint && !(showMeter && strength) ? (
        <p id={`${inputId}-hint`} className="text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
